import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';
import { trapFocus, removeTrapFocus } from '@theme/focus';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  get #trapFocus() {
    return this.dataset.trapFocus === 'true';
  }

  #controller = new AbortController();
  /** @type {MutationObserver | null} */
  #observer = null;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();
    this.#updateAriaExpanded();
    this.#handleFocusTrap();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });

    // Observe changes to the open attribute
    this.#observer = new MutationObserver(() => {
      this.#updateAriaExpanded();
      this.#handleFocusTrap();
    });

    this.#observer.observe(this.details, {
      attributes: true,
      attributeFilter: ['open'],
    });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
    if (this.#observer) {
      this.#observer.disconnect();
    }
    if (this.#trapFocus) {
      removeTrapFocus();
    }
  }

  /**
   * Updates the aria-expanded attribute based on the details open state
   */
  #updateAriaExpanded() {
    if (this.summary.hasAttribute('aria-expanded')) {
      this.summary.setAttribute('aria-expanded', this.details.open ? 'true' : 'false');
    }
  }

  /**
   * Handles focus trapping when accordion is open
   */
  #handleFocusTrap() {
    if (!this.#trapFocus) return;

    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Only trap focus when accordion is actually interactive (not disabled)
    const isInteractive = !((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop));

    if (this.details.open && isInteractive) {
      // Trap focus within the accordion when open and interactive
      trapFocus(this.details);
    } else {
      // Remove focus trap when closed or disabled
      removeTrapFocus();
    }
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
    this.#updateAriaExpanded();
    this.#handleFocusTrap();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape && this.details.open) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus({ preventScroll: true });
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
